<template>
    <div class="input-group">
        <InputText
            type="text"
            v-model="model"
            ref="input"
            :placeholder="placeholder"
            :class="$attrs.class"
            :disabled="disabled"
            @focus="emits('focus', $event)"
            @blur="onBlur($event)"
            @keydown="onKeyDown($event)"
        />
        <slot>
            <span class="input-group-text-inline d-flex align-items-center">
                <IconComp
                    v-if="!searchstate?.isActive"
                    icon="fa-magnifying-glass"
                ></IconComp>
                <IconSpinner
                    v-else
                    :state="searchstate"
                ></IconSpinner>
            </span>
        </slot>
    </div>
</template>
<script setup lang="ts">
import { IconComp, IconSpinner, InputText } from '@/components';
import type { IEditorComponent } from '@/objects/gui';
import type { ComponentPublicInstance } from 'vue';
import { ref } from 'vue';
import type { IEmitBlur } from '../Interfaces/IEmitBlur';
import type { IEmitFocus } from '../Interfaces/IEmitFocus';
import { isInputFinishedKey } from '@/utilities/keyboardutils';
import type { TaskLoaderState } from '@/composeables';

const input = ref<ComponentPublicInstance<IEditorComponent> | null>(null);

withDefaults(
    defineProps<{
        /**
         * The placeholder text
         * @default ''
         */
        placeholder?: string;

        /**
         * The state of the search
         * We use this to show a spinner in the search box
         *
         */
        searchstate?: TaskLoaderState;

        /**
         * If the input is disabled
         * @default undefined
         */
        disabled?: boolean;
    }>(),
    {
        placeholder: '',
    },
);
const model = defineModel<string>({ required: true });

// Define the emits
const emits = defineEmits<{ (e: 'searching', value: string, event: KeyboardEvent): void } & IEmitBlur & IEmitFocus>();

/**
 * On blur we emit the blur event and the searching event so that the parent can handle the search
 * We do this because it is not always sure you want to search when the input text changes, but instead when enter or blur is happening
 * @param event
 */
const onBlur = (event: Event) => {
    emits('blur', event);
};

const onKeyDown = (event: KeyboardEvent) => {
    if (isInputFinishedKey(event)) {
        emits('searching', model.value, event);
    }
};

// Exposed functions
defineExpose({
    /**
     * Focus the input
     */
    focus: () => input.value?.focus(),
});
</script>
